import { Link } from 'react-router-dom';
import { MapPin, Mail, Building2, FileText, Phone, MessageCircle } from 'lucide-react';
import ScrollReveal from '../components/ScrollReveal';

const Contact = () => {
  return (
    <main data-testid="contact-page">
      {/* Page Header */}
      <section className="page-header" data-testid="contact-header">
        <div className="container-main">
          <div className="breadcrumb">
            <Link to="/" data-testid="breadcrumb-home">Our Services</Link>
            <span>/</span>
            <span className="text-white">Contact</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-4 opacity-0 animate-fade-in-up">
            Contact Us
          </h1>
          <p className="text-white/70 text-base md:text-lg max-w-xl opacity-0 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            Your first consultation is completely free. Reach out and let us help you plan your journey.
          </p>
        </div>
      </section>

      {/* Free Consultation Banner */}
      <section className="py-6 bg-[var(--secondary)]" data-testid="free-consultation-banner">
        <div className="container-main">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-white font-bold text-sm uppercase tracking-wider">
              Free Consultation &mdash; No cost to ask, no pressure to commit
            </p>
            <a
              href="https://wa.me/8801338770055?text=Hi%2C%20I%27d%20like%20to%20book%20a%20free%20consultation."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-2 bg-white text-[var(--primary)] text-xs font-bold uppercase tracking-wider hover:bg-white/90 transition-colors"
              data-testid="contact-whatsapp-cta"
            >
              <MessageCircle size={16} />
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="section-padding bg-white" data-testid="contact-info-section">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Left Column - Info */}
            <ScrollReveal>
              <div>
                <p className="text-[var(--secondary)] text-xs font-bold uppercase tracking-[0.2em] mb-4">Get In Touch</p>
                <h2 className="text-3xl md:text-4xl font-bold text-[var(--primary)] tracking-tight mb-6">Contact Information</h2>
                <div className="gold-accent-line mb-8" />
                <div className="space-y-4">
                  <div className="contact-item" data-testid="contact-address">
                    <div className="w-12 h-12 flex items-center justify-center bg-[var(--background)] flex-shrink-0">
                      <MapPin size={22} className="text-[var(--secondary)]" />
                    </div>
                    <div>
                      <h4 className="font-bold text-[var(--primary)] mb-1">Office Location</h4>
                      <p className="text-[var(--text-secondary)] leading-relaxed">
                        Rupayan Shopping Square (4th Floor),<br />
                        Plot No. 02, Sayem Sobhan Anvir Road,<br />
                        Bashundhara R/A, Dhaka-1229, Bangladesh
                      </p>
                    </div>
                  </div>
                  <div className="contact-item" data-testid="contact-phone">
                    <div className="w-12 h-12 flex items-center justify-center bg-[var(--background)] flex-shrink-0">
                      <Phone size={22} className="text-[var(--secondary)]" />
                    </div>
                    <div>
                      <h4 className="font-bold text-[var(--primary)] mb-1">Phone</h4>
                      <p className="text-[var(--text-secondary)] text-sm">
                        <a href="tel:+8801338770055" className="hover:text-[var(--secondary)] transition-colors">+880 1338-770055</a>
                      </p>
                    </div>
                  </div>
                  <div className="contact-item" data-testid="contact-email">
                    <div className="w-12 h-12 flex items-center justify-center bg-[var(--background)] flex-shrink-0">
                      <Mail size={22} className="text-[var(--secondary)]" />
                    </div>
                    <div>
                      <h4 className="font-bold text-[var(--primary)] mb-1">Email Address</h4>
                      <a href="mailto:daasglobalsub@gmail.com" className="text-[var(--text-secondary)] hover:text-[var(--secondary)] transition-colors" data-testid="email-link">
                        daasglobalsub@gmail.com
                      </a>
                    </div>
                  </div>
                  <div className="contact-item" data-testid="contact-whatsapp">
                    <div className="w-12 h-12 flex items-center justify-center bg-[var(--background)] flex-shrink-0">
                      <MessageCircle size={22} className="text-[var(--secondary)]" />
                    </div>
                    <div>
                      <h4 className="font-bold text-[var(--primary)] mb-1">WhatsApp</h4>
                      <a href="https://wa.me/8801338770055" target="_blank" rel="noopener noreferrer" className="text-[var(--text-secondary)] hover:text-[var(--secondary)] transition-colors" data-testid="whatsapp-link">
                        +880 1338-770055
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            {/* Right Column */}
            <ScrollReveal delay={150}>
              <div className="space-y-6">
                <div className="governance-card" data-testid="parent-company-card">
                  <div className="flex items-center gap-3 mb-4">
                    <Building2 size={20} className="text-[var(--secondary)]" />
                    <h4 className="font-bold text-[var(--primary)]">Parent Company</h4>
                  </div>
                  <p className="text-[var(--text-secondary)] leading-relaxed">
                    DAAS Global operates as a subsidiary of DAAS Limited under formal corporate governance.
                  </p>
                </div>
                <div className="governance-card" data-testid="careers-note-card">
                  <div className="flex items-center gap-3 mb-4">
                    <FileText size={20} className="text-[var(--secondary)]" />
                    <h4 className="font-bold text-[var(--primary)]">Career Opportunities</h4>
                  </div>
                  <p className="text-[var(--text-secondary)] leading-relaxed mb-4">
                    All roles operate under formal contracts and internal policies.
                  </p>
                  <Link to="/careers" className="text-[var(--secondary)] font-medium hover:text-[var(--primary)] transition-colors" data-testid="careers-link">
                    Learn about our work culture &rarr;
                  </Link>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Map & Building Section */}
      <ScrollReveal>
        <section className="bg-[var(--background)]" data-testid="map-section">
          <div className="container-main py-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Map */}
              <div className="aspect-[4/3] lg:aspect-auto lg:min-h-[400px] bg-[var(--primary)] relative overflow-hidden" data-testid="map-container">
                <iframe
                  src="https://www.google.com/maps?q=Rupayan+Shopping+Square,+Bashundhara+R/A,+Dhaka+1229,+Bangladesh&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="DAAS Global Office - Rupayan Shopping Square, Bashundhara R/A"
                  className="grayscale-[30%] opacity-90"
                  data-testid="map-iframe"
                />
              </div>

              {/* Office Building Image */}
              <div className="aspect-[4/3] lg:aspect-auto lg:min-h-[400px] relative overflow-hidden" data-testid="office-building-container">
                <img
                  src="https://customer-assets.emergentagent.com/job_ba4b6351-8570-4d37-8ada-e860f15e5a88/artifacts/c18ijl0a_IMG_2216.jpeg"
                  alt="DAAS Global Office - Rupayan Shopping Square, Bashundhara R/A, Dhaka"
                  className="w-full h-full object-cover"
                  data-testid="office-building-image"
                />
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>
    </main>
  );
};

export default Contact;
